import React, { useState, useEffect } from "react"
import { DeleteOutline } from "@material-ui/icons"
import {
  Radio,
  FormControlLabel,
  RadioGroup,
  makeStyles,
  TextField,
  Box,
  Grid,
  Typography,
  Hidden,
} from "@material-ui/core"
import { ExpansionPanel, KnowledgeContentTypeIcon, RemoveIconButton, UploaderDropZone } from ".."
import { KNOWLEDGE_CONTENT_TYPE } from "../../data"
import { UploadingList, UploadingImagesList } from "../Uploader"
import { KnowledgeArticleContentVideo } from "./KnowledgeArticleContentVideo"

const useStyles = makeStyles((theme) => ({
  contentTypeIcon: {
    color: theme.palette.text.light,
  },
  content: {
    border: `1px solid ${theme.palette.shaded.border}`,
    borderRadius: 4,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  contentField: {
    backgroundColor: "#ffffff",
  },
  hint: {
    color: theme.palette.text.light,
  },
}))

const knowledgeContentTypeLabels = {
  [KNOWLEDGE_CONTENT_TYPE.TEXT]: "Text",
  [KNOWLEDGE_CONTENT_TYPE.IMAGE]: "Image",
  [KNOWLEDGE_CONTENT_TYPE.NOTE]: "Note (Information)",
  [KNOWLEDGE_CONTENT_TYPE.NOTE_IMPORTANT]: "Note (Important)",
  [KNOWLEDGE_CONTENT_TYPE.NOTE_DANGER]: "Note (Danger)",
  [KNOWLEDGE_CONTENT_TYPE.FILES]: "Files",
  [KNOWLEDGE_CONTENT_TYPE.VIDEO]: "Media",
}

const textRows = 10

const KnowledgeArticleCreatorContent = ({
  contentIndex: index,
  content,
  onToggleExpanded,
  onUpdateContent,
  onUpdateContentValue,
  onRemoveContent,
}) => {
  const classes = useStyles()

  const [uploads, setUploads] = useState([])
  const [uploading, setUploading] = useState([])
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    if (content.uploads) {
      setUploads([...content.uploads])
    }
  }, [content.uploads])

  const handleBeforeUpload = (items) => {
    setIsUploading(true)
    setUploading(() => items)
  }
  const handleUploadProgress = (items) => {
    setUploading(() => items)
  }
  const handleItemUploaded = (item) => {
    setUploads((state) => [...state, item])
  }
  const handleRemoveUpload = (id) => {
    // setUploads([...uploads.filter(upload => upload.id !== id)])
    onUpdateContent(index, "uploads", [...uploads.filter((upload) => upload.id !== id)])
  }
  const handleAfterUpload = (items) => {
    onUpdateContent(index, "uploads", [...uploads, ...items.map((item) => item.upload)])
  }
  const handleNotUploading = () => {
    setIsUploading(false)
  }

  return (
    <ExpansionPanel
      {...{
        icon: <KnowledgeContentTypeIcon type={content.type} fontSize="large" className={classes.contentTypeIcon} />,
        title: content.heading,
        description: knowledgeContentTypeLabels[content.type],
        sortable: true,
        expanded: content.expanded,
        onChange: () => onToggleExpanded(index),
        className: classes.content,
        placeholders: { title: "Untitled" },
        TransitionProps: { unmountOnExit: true },
      }}
    >
      <Box flexDirection="column" flexGrow={1} pl={3} pr={3}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <TextField
              variant="outlined"
              fullWidth
              label="Heading"
              value={content.heading}
              className={classes.contentField}
              onChange={(e) => onUpdateContent(index, "heading", e.target.value)}
              data-cy="KnowledgeArticleCreatorContent-TextField-heading"
            />
          </Grid>
          <Grid item>
            {content.type === KNOWLEDGE_CONTENT_TYPE.TEXT && (
              <>
                <TextField
                  variant="outlined"
                  value={content.value}
                  fullWidth
                  size="small"
                  label="Text"
                  multiline
                  minRows={textRows}
                  onChange={(e) => onUpdateContentValue(index, e.target.value)}
                  className={classes.contentField}
                  data-cy="KnowledgeArticleCreatorContent-TextField-text"
                />
              </>
            )}
            {content.type === KNOWLEDGE_CONTENT_TYPE.IMAGE && (
              <Box>
                <UploadingImagesList
                  uploaded={content.uploads}
                  uploading={uploading}
                  onRemoveUpload={handleRemoveUpload}
                  mb={2}
                />
                <UploaderDropZone
                  onBeforeUpload={handleBeforeUpload}
                  onProgress={handleUploadProgress}
                  onItemUploaded={handleItemUploaded}
                  onAfterUpload={handleAfterUpload}
                  onError={handleNotUploading}
                  documents={false}
                  disabled={isUploading}
                />
              </Box>
            )}
            {[
              KNOWLEDGE_CONTENT_TYPE.NOTE,
              KNOWLEDGE_CONTENT_TYPE.NOTE_IMPORTANT,
              KNOWLEDGE_CONTENT_TYPE.NOTE_DANGER,
            ].includes(content.type) && (
              <>
                <RadioGroup
                  value={content.type}
                  onChange={(e) => onUpdateContent(index, "type", e.target.value - 0)}
                  row
                >
                  <FormControlLabel
                    label="Information"
                    value={KNOWLEDGE_CONTENT_TYPE.NOTE}
                    control={<Radio color="primary" />}
                  />
                  <FormControlLabel
                    label="Important"
                    value={KNOWLEDGE_CONTENT_TYPE.NOTE_IMPORTANT}
                    control={<Radio color="primary" />}
                  />
                  <FormControlLabel
                    label="Danger"
                    value={KNOWLEDGE_CONTENT_TYPE.NOTE_DANGER}
                    control={<Radio color="primary" />}
                  />
                </RadioGroup>
                <TextField
                  variant="outlined"
                  value={content.value}
                  fullWidth
                  size="small"
                  label="Note"
                  multiline
                  minRows={5}
                  onChange={(e) => onUpdateContentValue(index, e.target.value)}
                  className={classes.contentField}
                />
              </>
            )}
            {content.type === KNOWLEDGE_CONTENT_TYPE.FILES && (
              <Box>
                <UploadingList
                  uploaded={content.uploads}
                  uploading={uploading}
                  onRemoveUpload={handleRemoveUpload}
                  mb={2}
                />
                <UploaderDropZone
                  onBeforeUpload={handleBeforeUpload}
                  onProgress={handleUploadProgress}
                  onItemUploaded={handleItemUploaded}
                  onAfterUpload={handleAfterUpload}
                  onError={handleNotUploading}
                  images={false}
                  disabled={isUploading}
                />
              </Box>
            )}
            {content.type === KNOWLEDGE_CONTENT_TYPE.VIDEO && (
              <>
                <TextField
                  variant="outlined"
                  value={content.value}
                  fullWidth
                  label="Media URL"
                  onChange={(e) => onUpdateContentValue(index, e.target.value)}
                  className={classes.contentField}
                />
                <Typography variant="caption" className={classes.hint}>
                  <Box mr={1}>Supported: YouTube, Vimeo, SoundCloud, Mixcloud</Box>
                </Typography>
                {content.value && (
                  <Hidden smDown>
                    <Box mt={1}>
                      <KnowledgeArticleContentVideo value={content.value} />
                    </Box>
                  </Hidden>
                )}
              </>
            )}
          </Grid>
        </Grid>
        <Box mt={1} display="flex">
          <Box ml="auto">
            <RemoveIconButton onConfirmedClick={() => onRemoveContent(index)} subject="content">
              <DeleteOutline />
            </RemoveIconButton>
          </Box>
        </Box>
      </Box>
    </ExpansionPanel>
  )
}

export { KnowledgeArticleCreatorContent }
